<template>
  <div v-if="available" class="hdrEdits" v-click-outside="close">
    <!-- Läuft etwas → Ladekreis + Anzahl; alles fertig → „fertig/gestartet", bis das Menü geöffnet wird -->
    <button type="button" class="hdrEdits__trigger" :class="{ open, busy: myRunning.length, fresh: !myRunning.length && doneTotal, failed: !myRunning.length && doneTotal && doneOk < doneTotal }" :title="title" @click="toggle">
      <v-progress-circular v-if="myRunning.length" indeterminate size="16" width="2" color="#7b5cd6" />
      <v-icon v-else small>{{ doneTotal ? (doneOk < doneTotal ? "mdi-alert-circle-outline" : "mdi-check-circle-outline") : "mdi-image-edit-outline" }}</v-icon>
      <span>Edits</span>
      <span v-if="myRunning.length" class="hdrEdits__badge">{{ myRunning.length }}</span>
      <span v-else-if="doneTotal" class="hdrEdits__badge hdrEdits__badge--done">{{ doneOk }}/{{ doneTotal }}</span>
    </button>

    <div v-if="open" class="hdrEdits__dropdown">
      <div class="hdrEdits__head">
        <b>Bild-Edits</b>
        <v-spacer />
        <div class="hdrEdits__seg">
          <button type="button" :class="{ on: !showAll }" @click="showAll = false">Meine</button>
          <button type="button" :class="{ on: showAll }" @click="showAll = true">Alle</button>
        </div>
      </div>

      <div class="hdrEdits__section">Läuft <span class="hdrEdits__muted">({{ runningList.length }})</span></div>
      <div v-if="!runningList.length" class="hdrEdits__empty">Gerade läuft kein Edit.</div>
      <div v-for="j in runningList" :key="j.id" class="hdrEdits__row" @click="openJob(j)">
        <v-progress-circular indeterminate size="14" width="2" color="#7b5cd6" class="hdrEdits__icon" />
        <div class="hdrEdits__main">
          <div class="hdrEdits__line1"><b>{{ orderName(j) }}</b> · {{ j.label }}</div>
          <div v-if="j.text" class="hdrEdits__text">{{ j.text }}</div>
        </div>
        <div class="hdrEdits__meta">{{ j.status === "queued" ? "wartet" : runningFor(j) }}<template v-if="showAll && j.by"><br />{{ byLabel(j.by) }}</template></div>
      </div>

      <div class="hdrEdits__section">
        Fertig <span class="hdrEdits__muted">(letzte 2 h)</span>
        <v-spacer />
        <button v-if="unseenDone.length" type="button" class="hdrEdits__link" @click="markAllSeen">alle gesehen</button>
      </div>
      <div v-if="!finishedList.length" class="hdrEdits__empty">Noch nichts fertig.</div>
      <div v-for="j in finishedList" :key="j.id" class="hdrEdits__row" :class="{ 'hdrEdits__row--unseen': !seen[j.id], 'hdrEdits__row--failed': j.status === 'failed' }" @click="openJob(j)">
        <v-icon small class="hdrEdits__icon" :color="j.status === 'failed' ? '#b23a31' : '#2e7d32'">{{ j.status === "failed" ? "mdi-alert-circle-outline" : "mdi-check-circle-outline" }}</v-icon>
        <div class="hdrEdits__main">
          <div class="hdrEdits__line1"><b>{{ orderName(j) }}</b> · {{ j.label }}</div>
          <div v-if="j.status === 'failed' && j.error" class="hdrEdits__text hdrEdits__text--err">{{ j.error }}</div>
          <div v-else-if="j.warning" class="hdrEdits__text hdrEdits__text--warn">Prüfen: {{ j.warning }}</div>
          <div v-else-if="j.text" class="hdrEdits__text">{{ j.text }}</div>
        </div>
        <div class="hdrEdits__meta">
          <span v-if="j.scoreAfter !== null" class="hdrEdits__score" :style="{ color: scoreColor(j.scoreAfter) }">{{ j.scoreAfter }}</span>
          {{ ago(j.finishedAtMs) }}<template v-if="showAll && j.by"><br />{{ byLabel(j.by) }}</template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Header-Knopf „Edits": laufende und frisch fertige Bild-Edits (Bereich/Feedback/KI) über alle Seiten hinweg.
// Klick auf einen Eintrag öffnet die Bestellung; fertige bleiben markiert, bis sie angeklickt wurden.
import { mapGetters } from "vuex";
import { auth } from "../../fb";
import { editJobs, startEditJobs, markSeen, markAllSeen, ackEdits, orderUidOf } from "@/editJobs";
import { openOrderModal, getDateFromNowShort } from "@/utils";
import { clock } from "@/clock";
import { scoreColor } from "@/upscaleJobs";

export default {
  name: "HeaderEdits",
  data: () => ({ open: false, showAll: false }),
  computed: {
    ...mapGetters(["allUsers"]),
    me() {
      return (auth.currentUser && auth.currentUser.uid) || null;
    },
    available() {
      return editJobs.available;
    },
    seen() {
      return editJobs.seen;
    },
    runningList() {
      return editJobs.running.filter((j) => this.showAll || j.by === this.me);
    },
    finishedList() {
      return editJobs.finished.filter((j) => this.showAll || j.by === this.me).slice(0, 30);
    },
    unseenDone() {
      return editJobs.finished.filter((j) => j.by === this.me && !editJobs.seen[j.id]);
    },
    /** Header-Badge: nur eigene Edits (unabhängig vom Meine/Alle-Schalter im Menü) */
    myRunning() {
      return editJobs.running.filter((j) => j.by === this.me);
    },
    finishedSinceAck() {
      return editJobs.finished.filter((j) => j.by === this.me && (j.finishedAtMs || 0) > editJobs.ackAt);
    },
    doneTotal() {
      return this.finishedSinceAck.length;
    },
    doneOk() {
      return this.finishedSinceAck.filter((j) => j.status === "done").length;
    },
    title() {
      if (this.myRunning.length) return `${this.myRunning.length} Edit${this.myRunning.length === 1 ? "" : "s"} laufen`;
      if (this.doneTotal) return `${this.doneOk} von ${this.doneTotal} Edits fertig${this.doneOk < this.doneTotal ? ", Rest fehlgeschlagen" : ""} — Menü öffnen zum Ansehen`;
      return "Bild-Edits";
    },
  },
  mounted() {
    startEditJobs();
  },
  methods: {
    toggle() {
      this.open = !this.open;
      ackEdits();
    },
    close() {
      if (this.open) ackEdits();
      this.open = false;
    },
    orderName(j) {
      const uid = orderUidOf(j);
      return editJobs.orderNames[uid] || uid || "–";
    },
    openJob(j) {
      markSeen(j.id);
      const uid = orderUidOf(j);
      if (uid) openOrderModal(uid);
      this.open = false;
    },
    markAllSeen() {
      markAllSeen();
    },
    runningFor(j) {
      const from = j.startedAtMs || j.createdAtMs;
      if (!from) return "";
      const s = Math.max(0, Math.round((clock.now - from) / 1000));
      return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
    },
    ago(ms) {
      return ms ? getDateFromNowShort(new Date(ms)) : "";
    },
    byLabel(by) {
      if (by === "autoRatingEdit") return "Auto-Edit";
      const u = Array.isArray(this.allUsers) ? this.allUsers.find((x) => x.uid === by) : null;
      return (u && (u.username || u.name)) || "Team";
    },
    scoreColor(v) {
      return scoreColor(v);
    },
  },
};
</script>

<style lang="scss" scoped>
.hdrEdits {
  position: relative;
  margin-right: 12px;
  &__trigger {
    display: inline-flex; align-items: center; gap: 6px; height: 40px; padding: 0 14px; border-radius: 999px;
    background: #f4f3f7; font-size: 14px; font-weight: 600; color: rgba(0, 0, 0, 0.65); transition: background 0.15s;
    &:hover, &.open, &.busy { background: #edeafa; color: #6a4fc4; }
    &.fresh { background: #e4f5eb; color: #2e7d32; .v-icon { color: #2e7d32 !important; } }
    &.failed { background: #fbe9e7; color: #b23a31; .v-icon { color: #b23a31 !important; } }
  }
  &__badge { min-width: 20px; height: 20px; padding: 0 6px; border-radius: 999px; background: #7b5cd6; color: #fff; font-size: 11px; font-weight: 700; line-height: 20px; text-align: center;
    &--done { background: #2e7d32; }
    .failed & { background: #b23a31; }
  }
  &__dropdown { position: absolute; top: 48px; right: 0; width: min(560px, 92vw); max-height: 70vh; overflow-y: auto; background: #fff; border-radius: 14px; box-shadow: 0 8px 30px rgba(16, 24, 40, 0.18); padding: 10px; z-index: 20; }
  &__head { display: flex; align-items: center; padding: 2px 6px 8px; }
  &__seg { display: inline-flex; background: #f1f2f6; border-radius: 999px; padding: 2px;
    button { border: 0; background: none; padding: 3px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; color: rgba(0, 0, 0, 0.55); cursor: pointer; }
    button.on { background: #fff; color: #282828; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12); }
  }
  &__section { display: flex; align-items: center; font-size: 11px; letter-spacing: 0.06em; text-transform: uppercase; color: rgba(0, 0, 0, 0.5); font-weight: 700; padding: 8px 6px 4px; }
  &__muted { font-weight: 500; margin-left: 4px; text-transform: none; letter-spacing: 0; }
  &__empty { font-size: 12px; color: rgba(0, 0, 0, 0.45); padding: 4px 6px 8px; }
  &__row { display: flex; align-items: flex-start; gap: 10px; padding: 8px 8px; border-radius: 10px; cursor: pointer;
    &:hover { background: #f6f5fb; }
    &--unseen { background: #f0faf3; &:hover { background: #e4f5eb; } }
    &--failed { background: #fdf1f0; }
  }
  &__icon { margin-top: 2px; flex-shrink: 0; }
  &__main { flex: 1; min-width: 0; }
  &__line1 { font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__text { font-size: 12px; color: rgba(0, 0, 0, 0.55); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    &--err { color: #b23a31; white-space: normal; }
    &--warn { color: #a86f00; }
  }
  &__meta { font-size: 11px; color: rgba(0, 0, 0, 0.5); text-align: right; white-space: nowrap; }
  &__score { font-weight: 700; margin-right: 4px; }
  &__link { border: 0; background: none; font-size: 11px; color: #6a4fc4; cursor: pointer; text-transform: none; letter-spacing: 0; font-weight: 600; }
}
</style>
